import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Search } from 'lucide-react';
import { cn } from '@proerp/ui';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { CreateSheet } from './CreateSheet';
import { currentPlaceLabel, exactLeaf, navLeaves, pinnedLeaves } from './navLeaves';
import { recentPlacesFor, useRecentPlaces } from './recentPlaces';
import { useNavGroupOrder, useNavItems } from './useNavItems';
import { WohinSheet } from './WohinSheet';

/**
 * Schwebender Knopf der Leiste. Glas-Optik über dem Inhalt; `pointer-events`
 * nur auf den Knöpfen, damit der Streifen dazwischen den Inhalt nicht blockiert.
 */
const FLOAT =
  'pointer-events-auto flex h-13 shrink-0 select-none touch-manipulation items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-[0_6px_20px_rgba(0,0,0,0.12)] backdrop-blur-md transition-colors active:bg-muted';

/**
 * Mobile Hauptnavigation (Variante B „Daumen-Suche", Tech-Lead 2026-09-24) —
 * ersetzt die frühere Bottom-Tab-Bar mit vier festen Plätzen und „Mehr"-Sheet.
 * Nur unterhalb des `tablet`-Breakpoints (744 px).
 *
 * Drei Ziele im Daumenbereich: Zurück · „Wohin?" (Suche + alle Bereiche) · „+"
 * (Anlegen). Warum: Die vier Plätze der alten Leiste waren schlicht die ersten
 * vier Einträge der Seitenleiste (Start, Analyse, Pool, Verkauf) — Projekte und
 * Zeiterfassung lagen hinter „Mehr", und „Mehr" zeigte 12 Bereiche als Reiter.
 *
 * Die Gesamthöhe bleibt wie bei der alten Leiste `4rem + Safe Area` (13 + 3
 * Einheiten): Klebende Fußleisten in Seiten (`sticky bottom-16`) und das Polster
 * des Inhalts in der AppShell rechnen damit.
 */
export function MobileDock() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sheet, setSheet] = useState<'wohin' | 'create' | null>(null);
  const navItems = useNavItems();
  const groupOrder = useNavGroupOrder();
  const userId = useCurrentUser().data?.data.id;
  const places = useRecentPlaces((state) => state.places);
  const remember = useRecentPlaces((state) => state.remember);

  const leaves = navLeaves(navItems);
  const recents = recentPlacesFor(places, userId);
  const label = currentPlaceLabel(location.pathname, leaves, recents);
  // Zurück nur, wenn es in der App einen vorigen Eintrag gibt: Der erste Eintrag
  // einer Sitzung trägt bei React Router den Schlüssel „default".
  const canGoBack = location.key !== 'default';

  // Module merken, sobald man auf ihrer Seite landet (egal über welchen Weg).
  // Über eine Ref, weil `useNavItems` je Render ein neues Array liefert.
  const leavesRef = useRef(leaves);
  leavesRef.current = leaves;
  useEffect(() => {
    if (!userId || location.pathname === '/') return;
    const leaf = exactLeaf(leavesRef.current, location.pathname, location.search);
    if (leaf) remember({ path: leaf.to, label: leaf.label, detail: leaf.parentLabel ?? leaf.group, userId });
  }, [location.pathname, location.search, userId, remember]);

  const pinned = useMemo(() => pinnedLeaves(navItems), [navItems]);
  const shownRecents = recents.filter((place) => place.path !== location.pathname).slice(0, 5);

  return (
    <>
      <nav
        aria-label="Hauptnavigation"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] tablet:hidden"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={!canGoBack}
            aria-label="Zurück"
            className={cn(FLOAT, 'w-13 disabled:opacity-40')}
          >
            <ChevronLeft className="size-6" strokeWidth={1.75} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setSheet('wohin')}
            aria-label={label ? `Wohin? Du bist in ${label}` : 'Wohin?'}
            aria-expanded={sheet === 'wohin'}
            className={cn(FLOAT, 'min-w-0 flex-1 justify-start gap-3 px-4 text-left')}
          >
            <Search className="size-5 shrink-0" strokeWidth={1.75} aria-hidden />
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="text-[15px] font-semibold">Wohin?</span>
              {label && <span className="truncate text-xs text-muted-foreground">{label}</span>}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setSheet('create')}
            aria-label="Neu anlegen"
            aria-expanded={sheet === 'create'}
            className={cn(FLOAT, 'w-13 border-transparent bg-foreground text-background active:bg-foreground/80')}
          >
            <Plus className="size-6" strokeWidth={1.75} aria-hidden />
          </button>
        </div>
      </nav>

      <WohinSheet
        open={sheet === 'wohin'}
        onOpenChange={(open) => setSheet(open ? 'wohin' : null)}
        navItems={navItems}
        groupOrder={groupOrder}
        leaves={leaves}
        pinned={pinned}
        recents={shownRecents}
        onPickRecord={(place) => {
          if (userId) remember({ ...place, userId });
        }}
      />
      <CreateSheet open={sheet === 'create'} onOpenChange={(open) => setSheet(open ? 'create' : null)} />
    </>
  );
}
